<template>
  <div class="page-container">
    <div class="content-wrap">
      <TopNavBar v-bind:secondTierLoadingDone="true"/>

      <b-container class="ga-text-page-container">
        <b-row>
          <b-col class="d-none d-md-block col-md-1 col-lg-2"></b-col>
          <b-col class="col-12 col-md-10 col-lg-8">
            <article class="ga-tool-page">
              <header class="ga-tool-page__header">
                <h1 class="ga-tool-page__title">{{ $t('Website Check') }}</h1>
                <p class="ga-tool-page__lead">{{ $t('You can check any website for genderable formulations') }}</p>
              </header>

              <section class="ga-tool-page__section">
                <label class="ga-form__label" for="website-check-url">{{ $t('URL') }}</label>
                <b-form @submit.prevent="parseAndGenderWebpage">
                  <input
                    id="website-check-url"
                    type="url"
                    class="ga-form__input"
                    :placeholder="$t('https://')"
                    v-model="urlToAnalyze"
                    autocomplete="off"
                  >
                </b-form>
                <p class="ga-form__hint" v-if="!$v.urlToAnalyze.required">
                  {{ $t('e.g. https://www.mobilitaet.bs.ch/gesamtverkehr/gut-zu-wissen/fussgaengerstreifen.html') }}
                </p>

                <div v-if="error" class="ga-form__error">{{ error }}</div>

                <div class="ga-tool-page__actions">
                  <button
                    type="button"
                    class="ga-form__btn"
                    v-on:click="parseAndGenderWebpage"
                    v-if="!queryRunning"
                  >{{ $t('Check') }}</button>
                  <button
                    type="button"
                    class="ga-form__btn"
                    disabled
                    v-if="queryRunning"
                  >{{ progress }} %</button>
                  <div class="ga-tool-page__progress" v-if="queryRunning">
                    <div class="ga-tool-page__progress-bar" :style="{ width: progress + '%' }"></div>
                  </div>
                </div>
              </section>

              <section class="ga-tool-page__section ga-tool-page__results">
                <h2 class="ga-tool-page__section-title">{{ $t('genderable terms') }}</h2>
                <div
                  class="ga-tool-page__results-body fixed-hight"
                  v-html="fixedWithUrls"
                ></div>
                <b-alert
                  v-if="tooLong"
                  show
                  variant="warning"
                  class="ga-tool-page__alert mt-2"
                  v-html="$t('Only 500 characters are processed. For longer texts please use the word add-in.')"
                ></b-alert>
              </section>

              <footer class="ga-tool-page__footer">
                <p>{{ $t('Here we analyse the first 5\'000 characters of the specified webpage') }}</p>
                <p>
                  {{ $t('if you want a monthly report for all pages of your webpresence, you might be interested in our commercial offer') }}:
                  <router-link :to="{ name: 'commercial' }">{{ $t('Commercial Use') }}</router-link>
                </p>
              </footer>

              <b-alert v-if="success" show variant="success" class="ga-tool-page__alert mt-2">{{ success }}</b-alert>
            </article>
          </b-col>
        </b-row>
      </b-container>

      <LoginWallModal
        ref="registerModal"
        :hint="$t('Website check only works while logged in')"
      />
    </div>
    <GenderAppFooter/>
  </div>
</template>

<script>
import TopNavBar from '../../components/TopNavBar'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import { DictionaryEntryService } from '@/services/dictionaryEntryService'
import { modalMixin } from '@/mixins/modalMixin'
import GenderAppFooter from '../../components/Footer'
import LoginWallModal from '../../components/LoginWallModal'
import { DOMAIN } from '@/env'

export default {
  name: 'WebsiteCheck',
  metaInfo () {
    return {
      title: this.title,
      meta: [
        {
          name: 'description',
          vmid: 'description',
          content: this.description
        },
        {
          property: 'og:title',
          vmid: 'og:title',
          content: this.title
        },
        {
          property: 'og:description',
          vmid: 'og:description',
          content: this.description
        },
        {
          property: 'og:url',
          vmid: 'og:url',
          content: this.canonicalUrl
        },
        {
          name: 'twitter:title',
          vmid: 'twitter:title',
          content: this.title
        },
        {
          name: 'twitter:description',
          vmid: 'twitter:description',
          content: this.description
        }
      ],
      link: [
        { rel: 'canonical', href: this.canonicalUrl }
      ]
    }
  },
  data () {
    return {
      queries: [],
      progress: 0,
      queryRunning: false,
      urlToAnalyze: '',
      genderableWords: '',
      fixedWithUrls: '',
      lektoratWaiting: 0,
      lektoratWorking: 0,
      error: '',
      success: '',
      text_cache: '',
      title: 'Website-Check · gender app',
      description: 'Analysiere Webseiten auf Wörter, die gegendert werden wollen',
      canonicalUrl: 'https://genderapp.org/website-check'
    }
  },
  computed: {
    tooLong: function () {
      return this.urlToAnalyze.length >= 500
    },
    urlToAnalyzeTrunc: function () {
      const re = new RegExp('^(?:(?:http[s]?|ftp):\\/)?\\/?(?:[^:\\/\\s]+?\\.)*([^:\\/\\s]+\\.[^:\\/\\s]+)')
      const match = re.exec(this.urlToAnalyze)
      if (!match || !match[1]) return this.urlToAnalyze
      const second = match[1]
      return (second.length > 20) ? second.substr(0, 20 - 1) + '...' : second
    }
  },
  mixins: [validationMixin, modalMixin],
  validations: {
    urlToAnalyze: {
      required
    }
  },
  methods: {
    cleanUpString (inputString, allowedCharacters = '[^a-zA-Z0-9öäüÖÄÜ\\s\\,\\.\\;\\n\\-\\?\\!\\*\\:\\/\\_]') {
      const regex = new RegExp('[^' + allowedCharacters + ']', 'g')
      return inputString.replace(regex, ' ')
    },
    sendLektorat: async function () {
      if (this.lektoratWaiting > 0) return Promise.resolve('Too many cooks')
      this.lektoratWaiting++
      await this.waitForSilence(0.1)
      this.lektoratWaiting--
      this.lektoratWorking++
      if (!this.urlToAnalyze.toString().trim()) {
        this.genderableWords = ''
        this.lektoratWorking--
        return Promise.resolve('empty paragraph')
      }
      await this.parseAndGenderWebpage()
      this.lektoratWorking--
      return Promise.resolve('ACK')
    },
    waitForSilence: async function (secs) {
      let noOfSecsWeWait = 0.2
      if (secs) {
        noOfSecsWeWait = secs
      }
      let disturbed = true
      while (disturbed) {
        disturbed = false
        await wait(noOfSecsWeWait * 1000)
        if (this.text_cache !== this.urlToAnalyze) {
          this.text_cache = this.urlToAnalyze
          disturbed = true
        }
      }
      return Promise.resolve('got it')
    },
    parseAndGenderWebpage: async function () {
      if (this.queryRunning) return
      this.error = ''
      this.genderableWords = ''
      this.fixedWithUrls = ''
      if (!this.urlToAnalyze) {
        this.error = this.$t('Please enter an URL')
        return
      }
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.error = this.$t('Please check above error(s).')
      } else {
        if (!this.isLoggedIn()) {
          this.showRegistrationModal()
          return
        }
        // eslint-disable-next-line no-control-regex
        const regex = new RegExp('https?:\\/\\/(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()!@:%_\\+.~#?&\\/\\/=]*)')
        if (!regex.test(this.urlToAnalyze)) {
          this.error = this.$t('please enter valid URL (starting with https:// or http://')
          return
        }
        if (this.urlToAnalyze.includes('genderapp.org')) {
          this.error = this.$t('very funny')
          return
        }
        this.progress = 0
        this.queryRunning = true
        const toSend = this.urlToAnalyze.substring(0, 500)
        let response
        try {
          response = await DictionaryEntryService.parseWebpage(toSend)
        } catch (err) {
          this.error = this.$t('We couldn\'t fetch the webpage. Maybe there are anti-fetching-rules in place. Please test another URL.')
          this.progress = 0
          this.genderableWords = ''
          this.fixedWithUrls = ''
          this.queryRunning = false
          return
        }
        const responseTrunc = response.data.result.substring(0, 30000)
        const responseClean = this.cleanUpString(responseTrunc)
        const re = /[^\s']+(?:\s+[^\s']+){0,20}/g
        const chunks = responseClean.match(re)
        const allGenderSpecifics = []
        for (let index = 0; index < chunks.length; index++) {
          this.progress = (index / chunks.length * 100).toFixed(0)
          const chunkResponse = await DictionaryEntryService.fixRunningText(chunks[index], "['N','Pro']", ['entries'])
          const messageFromBackend = JSON.parse(chunkResponse.data.result)
          allGenderSpecifics.push(...messageFromBackend.wordReplacer.entries.map(entry => entry.genderSpecific))
          this.genderableWords = [...new Set(allGenderSpecifics)]
          this.fixedWithUrls = '<div class="table-responsive"><table class="table table-sm table-hover mb-0"><tbody>' + this.genderableWords.map(e => `<tr><th style="min-width:110px">${e}</th><td style="min-width:110px" scope="row"><a href="${DOMAIN + encodeURI('/dict/' + e)}" target="_blank">Gender&shy;wörter&shy;buch&shy;eintrag <i class="fas fa-link text-secondary small"></i></a></td><td style="min-width:110px"><a href="${this.urlToAnalyze}#:~:text=${e}" target="_blank">Auf ${this.urlToAnalyzeTrunc} anzeigen <i class="fas fa-external-link-alt text-secondary small"></i><small class="ml-2 text-muted">(nur für Chrome & Edge)</small></a></td></tr>`).join(' ') + '</tbody></table></div>'
        }
        this.queryRunning = false
        this.error = ''
      }
    },
    showRegistrationModal () {
      this.$refs.registerModal.show()
    },
    hideRegistrationModal () {
      this.$refs.registerModal.hide()
    }
  },
  watch: {
    urlToAnalyze: function () {
      this.error = ''
    }
  },
  mounted () {
    if (!this.isLoggedIn()) {
      this.showRegistrationModal()
    }
  },
  components: { TopNavBar, GenderAppFooter, LoginWallModal }
}

async function wait (ms) {
  return new Promise(resolve => {
    setTimeout(resolve, ms)
  })
}
</script>

<style scoped>
.fixed-hight {
  min-height: 30vh;
  max-height: 50vh;
  overflow-y: auto;
  word-break: break-word;
}
</style>
